import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QuoteTemplate } from '@catalyst-psa/shared';
import {
  QUOTE_VIEWED_COOLDOWN_DEFAULT,
  QUOTE_VIEWED_COOLDOWN_MAX,
  QUOTE_VIEWED_COOLDOWN_MIN,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { useAuth } from '../../auth/authContext.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import {
  useCreateQuoteTemplate,
  useQuoteNotificationSettings,
  useQuoteTemplates,
  useUpdateQuoteNotificationSettings,
} from './api.js';

/** Admin list of quote templates (sections + product lines applied to quotes by type). */
export function QuoteTemplatesPage(): JSX.Element {
  const rowLink = useRowLink();
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuoteTemplates();
  const templates = query.data ?? [];

  return (
    <div>
      <PageHeader
        title="Quote Templates"
        description="Reusable quote content by Opportunity Type. Applying a template fills the quote's sections and adds its lines to the opportunity."
        actions={<Button onClick={() => setCreateOpen(true)}>New template</Button>}
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Opportunity Type</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={!query.isLoading && templates.length === 0}
              emptyTitle="No quote templates yet"
              emptyDescription="Create a template to reuse quote sections and product lines."
              colSpan={4}
            >
              {templates.map((template: QuoteTemplate) => (
                <tr
                  key={template.id}
                  {...rowLink(`/admin/quote-templates/${template.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-2 font-medium text-slate-800">{template.name}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {template.opportunityTypeName ?? 'Any type'}
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={template.isActive ? 'green' : 'gray'}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{formatDate(template.updatedAt)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      <CreateTemplateModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />

      <QuoteNotificationSettingsCard />
    </div>
  );
}

/**
 * Admin-only mailboxes notified about quote activity. These are global (not per template)
 * but live here because this is the quoting admin surface.
 */
function QuoteNotificationSettingsCard(): JSX.Element | null {
  const { isAdmin } = useAuth();
  const query = useQuoteNotificationSettings(isAdmin);
  const updateMutation = useUpdateQuoteNotificationSettings();
  const [approved, setApproved] = useState<string | null>(null);
  const [viewed, setViewed] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState<string | null>(null);

  // Server values seed the inputs until the admin types; nulls mean "not yet edited".
  const approvedValue = approved ?? query.data?.approvedNotifyEmail ?? '';
  const viewedValue = viewed ?? query.data?.viewedNotifyEmail ?? '';
  const cooldownValue =
    cooldown ??
    String(query.data?.viewedNotifyCooldownMinutes ?? QUOTE_VIEWED_COOLDOWN_DEFAULT);
  const dirty =
    (approved !== null && approved !== (query.data?.approvedNotifyEmail ?? '')) ||
    (viewed !== null && viewed !== (query.data?.viewedNotifyEmail ?? '')) ||
    (cooldown !== null &&
      cooldown !== String(query.data?.viewedNotifyCooldownMinutes ?? QUOTE_VIEWED_COOLDOWN_DEFAULT));

  if (!isAdmin) return null;

  const cooldownNumber = Number(cooldownValue);
  const cooldownInvalid =
    !Number.isInteger(cooldownNumber) ||
    cooldownNumber < QUOTE_VIEWED_COOLDOWN_MIN ||
    cooldownNumber > QUOTE_VIEWED_COOLDOWN_MAX;

  const error =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.isError
        ? 'Failed to save the notification emails.'
        : null;

  const handleSave = (): void => {
    updateMutation.mutate(
      {
        approvedNotifyEmail: approvedValue.trim(),
        viewedNotifyEmail: viewedValue.trim(),
        viewedNotifyCooldownMinutes: cooldownNumber,
      },
      {
        onSuccess: () => {
          setApproved(null);
          setViewed(null);
          setCooldown(null);
        },
      },
    );
  };

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Quote notification emails</h2>
          <p className="text-xs text-slate-500">
            Internal mailboxes copied on quote activity. Leave blank to turn a notification
            off.
          </p>
        </div>
        <Button
          size="sm"
          isLoading={updateMutation.isPending}
          disabled={!dirty || cooldownInvalid}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>

      {error ? (
        <p className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}
      {updateMutation.isSuccess && !dirty ? (
        <p className="mb-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Notification emails saved.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <TextField
            label="Quote approved notification email"
            type="email"
            value={approvedValue}
            disabled={query.isLoading}
            onChange={(e) => setApproved(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            Copied on the approval confirmation sent to the customer.
          </p>
        </div>
        <div>
          <TextField
            label="Quote viewed notification email"
            type="email"
            value={viewedValue}
            disabled={query.isLoading}
            onChange={(e) => setViewed(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            Emailed when the delivery page is opened.
          </p>
        </div>
        <div>
          <TextField
            label="Quote viewed cooldown (minutes)"
            type="number"
            min={QUOTE_VIEWED_COOLDOWN_MIN}
            max={QUOTE_VIEWED_COOLDOWN_MAX}
            step={1}
            value={cooldownValue}
            disabled={query.isLoading}
            onChange={(e) => setCooldown(e.target.value)}
            error={cooldownInvalid ? `Enter 0–${QUOTE_VIEWED_COOLDOWN_MAX}.` : undefined}
          />
          <p className="mt-1 text-xs text-slate-500">
            At most one &ldquo;viewed&rdquo; email per quote in this window, so a customer
            re-reading the page doesn&rsquo;t flood the mailbox. 0 emails every open.
          </p>
        </div>
      </div>
    </div>
  );
}

function CreateTemplateModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [opportunityTypeId, setOpportunityTypeId] = useState('');
  const categoriesQuery = useServiceCategoryOptions();
  const createMutation = useCreateQuoteTemplate();

  const error =
    createMutation.error instanceof ApiError
      ? createMutation.error.message
      : createMutation.isError
        ? 'Failed to create the template.'
        : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New quote template"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            isLoading={createMutation.isPending}
            disabled={!name.trim()}
            onClick={() =>
              createMutation.mutate(
                { name: name.trim(), opportunityTypeId: opportunityTypeId || null },
                { onSuccess: (created) => navigate(`/admin/quote-templates/${created.id}`) },
              )
            }
          >
            Create template
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Managed Services standard quote"
        />
        <SelectField
          label="Opportunity Type"
          value={opportunityTypeId}
          options={[
            { value: '', label: 'Any type' },
            ...(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
          onChange={(e) => setOpportunityTypeId(e.target.value)}
        />
        {error ? (
          <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        ) : null}
      </div>
    </Modal>
  );
}
